import React, { useEffect } from 'react';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Counter } from '@features/workspace/components/shared/counter';
import { Typography, Box } from '@mui/material';
import { useLocation } from 'react-router-dom';
import history from '@app/services/history';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles<any>()({
  controlsContainer: {
    // Ensure accordion toggle and add action appear above other elements below
    zIndex: '2 !important',
    marginTop: '-10px !important',
  },
});

type AccordionControlProps = {
  id: string;
  header: string;
  counter?: number | null;
  isCounterLoading?: boolean;
  button?: any | null;
  expandByDefault?: boolean | null;
  children: any;
};

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters square elevation={0} {...props} />
))(({ theme }) => ({
  border: `0px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&:before': {
    display: 'none',
  },
}));

export function CollapseGroup({
  id,
  header,
  counter,
  isCounterLoading = false,
  button,
  expandByDefault,
  children,
}: AccordionControlProps) {
  const { classes } = useStyles(null);

  const location = useLocation();

  let hashState = location.hash.slice(1).split(',');

  const [expanded, setExpanded] = React.useState<boolean>(
    hashState.includes(id)
  );

  const handleChange = (event: React.SyntheticEvent, isExpanded: boolean) => {
    toggleAccordion(isExpanded);
  };

  const toggleAccordion = (isExpanded: boolean) => {
    if (!isExpanded) {
      hashState = hashState.filter((hs: string) => hs !== id);
    } else if (!hashState.includes(id)) {
      hashState.push(id);
    }
    history.push(`#${hashState.filter((hs: string) => !!hs).join(',')}`);
    setExpanded(isExpanded);
  };

  if (expandByDefault === null) {
    expandByDefault = false;
  }

  useEffect(() => {
    if (expandByDefault) {
      toggleAccordion(true);
    }
    }, [expandByDefault]) //eslint-disable-line

  return (
    <>
      <Accordion expanded={expanded} onChange={handleChange}>
        <MuiAccordionSummary
          aria-controls={`${id}bh-content`}
          className={classes.controlsContainer}
          expandIcon={<ExpandMoreIcon style={{ color: 'black' }} />}
          id={`${id}bh-header`}
        >
          <Box
            alignItems="center"
            display="flex"
            flexDirection="row"
            gap={1}
            width="100%"
          >
            <Box
              display="flex"
              minWidth="140px"
              sx={{
                whiteSpace: 'nowrap',
              }}
            >
              <Typography variant="h6">{header}</Typography>
            </Box>
            {(counter !== undefined || isCounterLoading) && (
              <Box display="flex" ml={1}>
                <Counter isLoading={isCounterLoading} value={counter} />
              </Box>
            )}
            {button && (
              <Box alignItems="flex-end" display="flex" ml={2} width="100%">
                {button}
              </Box>
            )}
          </Box>
        </MuiAccordionSummary>
        <AccordionDetails sx={{ px: 0 }}>{children}</AccordionDetails>
      </Accordion>
    </>
  );
}
